/**
 * Phonological Awareness (4K) — six short standard-pattern assessments
 * (v3.70.0): Word Awareness, Rhyme Recognition, Alliteration, Syllable
 * Blending & Counting, Onset-Rime Matching, Beginning Sound Isolation.
 *
 * This is a DIFFERENT instrument from the five-step "Phonemic Awareness"
 * screener (src/constants/paScreenerItems.js). That screener is a
 * step-container instrument (parallel forms, windows, discontinue rule,
 * script cards); these six are ordinary standalone assessments following the
 * Letter/Sound pattern — one fixed form each, 5 items scored 0–5, quarter
 * stamped at submit, no windows, no timers, no discontinue logic. Ids carry
 * the `pa4k_` prefix so nothing here can collide with the screener's `pa_*`
 * identifiers or data.
 *
 * Nothing here is generated or inferred at runtime. The bank is a literal
 * transcription of the bank Kemal authored for Yilmaz Education LLC
 * (provenance: docs/assessments/ITEM_PROVENANCE.md, 2026-08-31 addendum).
 * src/constants/paScreenerItems.test.js extends the IP guard over this bank;
 * src/constants/pa4kItems.test.js pins bank integrity against the screener's
 * bank (three sanctioned model-word exceptions, nothing else) and the item
 * construction rules. The fix for any guard failure is always "Kemal picks a
 * different word", never an edit here and never a deleted assertion.
 */

export const PA4K_GROUP_NAME = 'Phonological Awareness (4K)';
export const PA4K_ITEMS_PER_TEST = 5;

// Administration order (also the Assessment page's test-picker order).
export const PA4K_TEST_TYPES = [
  'pa4k_word_awareness',
  'pa4k_rhyme_recognition',
  'pa4k_alliteration',
  'pa4k_syllables',
  'pa4k_onset_rime',
  'pa4k_beginning_sound',
];

/**
 * The item bank. Field shapes per test:
 *   word_awareness  — { sentence, words }         child counts words
 *   rhyme/allit     — { pair: [a, b], answer, trap? }  yes/no
 *   syllables       — { kind: 'blend', parts, word } | { kind: 'count', word, count }
 *   onset_rime      — { onset, rime, word }       teacher gives onset+rime
 *   beginning_sound — { word, sound }             child gives first sound
 */
export const PA4K_ITEMS = {
  pa4k_word_awareness: [
    { sentence: 'Birds fly.', words: 2 },
    { sentence: 'The boy runs.', words: 3 },
    { sentence: 'I see a cow.', words: 4 },
    { sentence: 'She has two pets.', words: 4 },
    { sentence: 'Frogs can swim.', words: 3 },
  ],
  pa4k_rhyme_recognition: [
    { pair: ['toad', 'road'], answer: 'yes' },
    // The "no" pairs deliberately share their first sound — a child matching
    // beginning sounds instead of endings is caught, not rewarded.
    { pair: ['lamb', 'lake'], answer: 'no' },
    { pair: ['mice', 'rice'], answer: 'yes' },
    { pair: ['peach', 'pool'], answer: 'no' },
    { pair: ['snail', 'whale'], answer: 'yes' },
  ],
  pa4k_alliteration: [
    { pair: ['pie', 'pony'], answer: 'yes' },
    // The mirror trap: the "no" pairs deliberately RHYME — a child who says
    // yes to these is matching endings, not beginnings.
    { pair: ['hose', 'rose'], answer: 'no', trap: 'rhyme' },
    { pair: ['table', 'toes'], answer: 'yes' },
    { pair: ['money', 'mitten'], answer: 'yes' },
    { pair: ['cape', 'grape'], answer: 'no', trap: 'rhyme' },
  ],
  pa4k_syllables: [
    { kind: 'blend', parts: ['spi', 'der'], word: 'spider' },
    { kind: 'blend', parts: ['win', 'dow'], word: 'window' },
    { kind: 'count', word: 'banana', count: 3 },
    { kind: 'count', word: 'wagon', count: 2 },
    // The one-syllable catch item.
    { kind: 'count', word: 'corn', count: 1 },
  ],
  pa4k_onset_rime: [
    { onset: '/f/', rime: 'arm', word: 'farm' },
    { onset: '/n/', rime: 'ail', word: 'nail' },
    { onset: '/k/', rime: 'ing', word: 'king' },
    { onset: '/t/', rime: 'own', word: 'town' },
    { onset: '/y/', rime: 'arn', word: 'yarn' },
  ],
  pa4k_beginning_sound: [
    { word: 'ball', sound: '/b/' },
    { word: 'song', sound: '/s/' },
    { word: 'toy', sound: '/t/' },
    { word: 'fire', sound: '/f/' },
    { word: 'nine', sound: '/n/' },
  ],
};

/**
 * Per-assessment teacher guidance — the ONE administration note each
 * assessment carries, rendered in the standard info line on the Assessment
 * page (the NWF description-box slot). Verbatim from the authored spec;
 * no script cards, no pacing, no discontinue machinery.
 */
export const PA4K_GUIDANCE = {
  pa4k_word_awareness: {
    howTo: 'Say the sentence once at normal speed. The child claps or moves a chip for each word, then tells you how many words. Correct = correct count.',
    model: '"Dogs bark." — two words (clap, clap).',
    practice: '"We eat lunch." (3)',
  },
  pa4k_rhyme_recognition: {
    howTo: "Say both words. The child answers yes if they rhyme, no if they don't. Keep your mouth out of the child's line of sight.",
    model: '"night, light — yes, they rhyme."',
    practice: '"moose, goose" (yes)',
  },
  pa4k_alliteration: {
    howTo: "Say both words. The child answers yes if they start with the same sound, no if they don't. Two of the 'no' pairs rhyme on purpose — a child who says yes to those is matching endings, not beginnings.",
    model: '"funny, fish — yes, both start with /f/."',
    practice: '"deer, dime" (yes)',
  },
  pa4k_syllables: {
    howTo: 'Items 1–2: say the word in parts with a clear pause; the child says the whole word. Items 3–5: say the word; the child claps the parts and tells you how many.',
    model: '"wa … ter — water." Then: "banana — ba-na-na, three claps."',
    practice: '"ba … by → baby"',
  },
  pa4k_onset_rime: {
    howTo: 'Say the first sound, a small pause, then the rest of the word. The child says the whole word. Say sounds, never letter names.',
    model: '"/h/ … at — hat."',
    practice: '"/r/ … ope" (rope)',
  },
  pa4k_beginning_sound: {
    howTo: 'Say the word; the child repeats it and gives the very first sound. Accept the sound, never the letter name. Keep your mouth out of the child\'s line of sight.',
    model: '"dog — dog starts with /d/."',
    practice: '"jar" (/j/)',
  },
};

/**
 * Every model/practice word above, as data, so tests never parse prose.
 * pa4kItems.test.js asserts each of these words really appears in the
 * PA4K_GUIDANCE strings — the list cannot silently drift from the text.
 * Models are demonstrated by the teacher and are NEVER scored.
 */
export const PA4K_MODEL_PRACTICE_WORDS = {
  pa4k_word_awareness: ['dogs', 'bark', 'we', 'eat', 'lunch'],
  pa4k_rhyme_recognition: ['night', 'light', 'moose', 'goose'],
  pa4k_alliteration: ['funny', 'fish', 'deer', 'dime'],
  pa4k_syllables: ['water', 'banana', 'baby'],
  pa4k_onset_rime: ['hat', 'rope'],
  pa4k_beginning_sound: ['dog', 'jar'],
};

/**
 * The ONLY words allowed to appear in BOTH this group and the existing
 * Phonemic Awareness screener bank: Kemal-supplied model examples — models
 * only, never scored in this group ("fish" is a scored Form B rhyme-pair
 * member there; "hat" a Form C onset item; "dog" a Form B rhyme-production
 * item). pa4kItems.test.js pins this list exactly; any OTHER collision fails
 * the build and goes back to Kemal — never edit a word to make it pass.
 */
export const PA4K_MODEL_EXCEPTIONS = ['dog', 'fish', 'hat'];

// ─── Bands ───────────────────────────────────────────────────────────────────
//
// Three bands on the 0–5 scale, using the Dashboard's standard band names
// (Mastery / Emerging / Needs Support — Kemal-confirmed 2026-08-31).
// Deliberately NOT the screener's two-band Developing/Proficient scale:
// different instrument, different reporting decision. Computed at display
// time like every standard assessment — never persisted on the doc.
export const PA4K_BANDS = [
  { id: 'mastery', label: 'Mastery', min: 4, max: 5 },
  { id: 'emerging', label: 'Emerging', min: 2, max: 3 },
  { id: 'needs_support', label: 'Needs Support', min: 0, max: 1 },
];

/** Band for a score, or null for empty / the -1 N/A sentinel. */
export function pa4kBandForScore(score) {
  if (score === '' || score === null || score === undefined) return null;
  const n = Number(score);
  if (!Number.isFinite(n) || n < 0) return null; // includes correctCount: -1 (N/A)
  return PA4K_BANDS.find((b) => n >= b.min && n <= b.max) || null;
}

/**
 * Canonical response-map key for an item — the stimulus itself, mirroring
 * how the standard pattern keys responses by the letter/word shown. These
 * keys are the doc's `responses` map keys and the Results detailed-table
 * columns, so they must stay stable.
 */
export function pa4kItemKey(testType, item) {
  if (testType === 'pa4k_word_awareness') return item.sentence;
  if (item.pair) return item.pair.join(', ');
  return item.word;
}

/** Teacher-screen prompt text for an item (what the teacher says/does). */
export function pa4kItemPrompt(testType, item) {
  if (testType === 'pa4k_word_awareness') return item.sentence;
  if (item.pair) return item.pair.join(', ');
  if (testType === 'pa4k_syllables') {
    return item.kind === 'blend' ? `${item.parts.join(' … ')} →` : item.word;
  }
  if (testType === 'pa4k_onset_rime') return `${item.onset} … ${item.rime}`;
  return item.word;
}

/** Expected-answer text shown to the teacher under the prompt. */
export function pa4kItemAnswer(testType, item) {
  if (testType === 'pa4k_word_awareness') return `${item.words} words`;
  if (testType === 'pa4k_alliteration' && item.trap === 'rhyme') return 'no (rhyme trap)';
  if (item.pair) return item.answer;
  if (testType === 'pa4k_syllables') {
    return item.kind === 'blend' ? item.word : String(item.count);
  }
  if (testType === 'pa4k_onset_rime') return item.word;
  return item.sound;
}

/**
 * Every spoken scored token in the bank, tagged with its testType — pair
 * members, syllable parts and whole words, onset-rime rimes and whole words,
 * and each Word Awareness sentence word. This is the surface the IP guard
 * and the bank-integrity tests scan. Word lists here are fixed by design
 * (the screener precedent): a future per-district/teacher custom-list
 * capability would slot in as a resolver in front of PA4K_ITEMS (the
 * getScreenerWords pattern) without changing the doc shape.
 */
export function collectPa4kScoredWords() {
  const norm = (w) => String(w).trim().toLowerCase().replace(/[.,!?]/g, '');
  const out = [];
  PA4K_TEST_TYPES.forEach((testType) => {
    PA4K_ITEMS[testType].forEach((item, index) => {
      const where = `${testType}[${index}]`;
      const push = (w) => out.push({ word: norm(w), where, testType });
      if (item.sentence) item.sentence.split(/\s+/).forEach(push);
      if (item.pair) item.pair.forEach(push);
      if (item.parts) item.parts.forEach(push);
      if (item.rime) push(item.rime);
      if (item.word) push(item.word);
    });
  });
  return out;
}
